import { useEffect, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import WideContainer from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { WithSkeleton, WithSkeletonContext } from '@r1-frontend/ui-react/components/loaders/Skeleton';
import NukaCarousel from '@r1-frontend/ui-react/components/NukaCarousel';
import { ArrowUp } from '@r1-frontend/ui-react/components/svg/arrows';
import { H2 } from '@r1-frontend/ui-react/components/typography/heading';
import {
    IndentContainer,
    ListContainer,
} from '@r1-frontend/ui-react/experimental/containers';
import { BorderRadius } from '@r1-frontend/ui-react/tokens/borderRadius';

import { Drawer } from '~/src/components/Drawer';
import AuthFormPopup from '~/src/components/PopupForm';
import { flushState, setAuthFormPopup, setShopItemId } from '~/src/entities/vases/PartnerServices/actions';
import ActionPopup from '~/src/entities/vases/PartnerServices/components/popups';
import ActionResultPopup from '~/src/entities/vases/PartnerServices/components/popups/ActionResult';
import StopperPopup from '~/src/entities/vases/PartnerServices/components/popups/Stopper';
import ServiceDetails from '~/src/entities/vases/PartnerServices/components/ServiceDetails';
import {
    useContextDispatch,
    useContextSelector,
} from '~/src/entities/vases/PartnerServices/hooks';
import Services from '~/src/entities/vases/PartnerServices/Services';
import { setCategory, toggleShowAllCategoryItems } from '~/src/entities/vases/store/actions';
import { loadSubscriptions } from '~/src/entities/vases/store/actions/thunk';
import { TFilterCategory, TSpecificVas, TSubscription, TVasService } from '~/src/entities/vases/store/reducer';
import { selectAvailableServicesByCategory } from '~/src/entities/vases/store/selectors';
// TODO - убрать импорты из модуля domru-tv, перенести в entities
import MultibundleCard from '~/src/features/domru-tv/packages/components/MultiBundleCard';
import { useRouterChange } from '~/src/helpers/hooks/useRouterChange';
import { useValueByBreakpoint } from '~/src/helpers/hooks/useValueByBreakpoint';
import { TState } from '~/src/store';
import { selectIsAuth } from '~/src/store/selectors/auth/selectIsAuth';

import showcaseAnalytics from './analytics';
import AuthBanner from './AuthBanner';
import Category from './Category';
import ServiceCategory from './ServiceCategory';
import ShowcaseSkeleton from './ShowcaseSkeleton';

type TShowcaseProps = {
    className?: string,
}

type TFilter = {
    category: TFilterCategory,
    title: string,
}

type TQuery = {
    alias?: [TFilterCategory],
    shopItemId?: string,
}

const filters: TFilter[] = [
    { category: 'tvPackages', title: 'Пакеты ТВ-каналов' },
    { category: 'partnerService', title: 'Полезные сервисы' },
    { category: 'antiviruses', title: 'Кибер-безопасность' },
    { category: 'subscriptions', title: 'Онлайн-кинотеатры' },
    { category: 'speedBonuses', title: 'Скоростные бонусы' },
    { category: 'additionalTVAndMovixServices', title: 'Доп. сервисы ТВ и Movix' },
];

const hasVasByShopItemId = (subscriptions: TSubscription[], shopItemId: number): boolean => {
    return subscriptions.some(item => item.shopItemId === shopItemId);
};

const hasVasInConnected = (vas: TVasService, shopItemId: number): boolean => {
    return Object.values(vas).some(item => hasVasByShopItemId(item.items, shopItemId));
};

const hasVasInAvailable = (specificVases: TSpecificVas[], shopItemId: number): boolean => {
    return specificVases.some(specificVas => hasVasByShopItemId(specificVas.items, shopItemId));
};

const Showcase = ({ className }: TShowcaseProps) => {
    const dispatch = useDispatch();
    const contextDispatch = useContextDispatch();

    const { isShowDetails, authFormPopup } = useContextSelector(state => ({
        isShowDetails: state.isShowDetails,
        authFormPopup: state.authFormPopup,
    }));

    const {
        availableServices: {
            loading,
            specific,
            selectedCategory,
        },
        connected,
        loaded,
        isAuth,
    } = useSelector((state: TState) => ({
        availableServices: selectAvailableServicesByCategory(state),
        connected: state.vasReducer.connected,
        loaded: state.vasReducer.loaded,
        isAuth: selectIsAuth(state),
    }), shallowEqual);

    const {
        query: {
            alias = [null],
            shopItemId,
        },
        replace,
        push,
    } = useRouter<TQuery>();

    const scrollPosition = useRef(0);

    useEffect(() => {
        const [category] = alias;

        dispatch(loadSubscriptions({
            withMultibundle: true,
            withWatchControl: true,
            selectedCategory: category,
        }));
    }, []);

    useEffect(() => {
        if (!Number(shopItemId) && !loaded) {
            return;
        }
        if (hasVasInAvailable(specific, Number(shopItemId))) {
            contextDispatch(setShopItemId(Number(shopItemId)));
        }
    }, [loaded]);

    useEffect(() => {
        if (!Number(shopItemId)) {
            return;
        }
        if (hasVasInConnected(connected, Number(shopItemId))) {
            push(`/lk/vases/?shopItemId=${shopItemId}`);
        }
    }, [connected, push, shopItemId]);

    useRouterChange({
        onStart: (next) => {
            const matches = next.match(/\/subscriptions\/(\w+)?/);
            const category = matches
                ? matches[1] as TFilterCategory | null
                : null;

            dispatch(setCategory(
                selectedCategory === category ? null : category,
            ));
        },
    });

    const count = useValueByBreakpoint({
        desktop: 4,
        sDesktop: 3,
        laptop: 2,
    });

    const handleToggleShowAll = (service: TSpecificVas) => {
        if (!service?.category) {
            return;
        }

        if (!service.showAll) {
            scrollPosition.current = window.scrollY;
            showcaseAnalytics.showSpecificCategoryImpressions({
                category: service.category,
                items: service.items,
                list: 'all',
                start: count,
            });
        } else {
            window.scrollTo({
                top: scrollPosition.current,
                behavior: 'smooth',
            });
        }

        dispatch(toggleShowAllCategoryItems({
            category: service.category,
            value: !service.showAll,
        }));
    };

    const handleCloseDrawer = async() => {
        contextDispatch(flushState());
        scrollPosition.current = window.scrollY;

        await replace(`/subscriptions/${selectedCategory || ''}`, undefined, { shallow: true });

        window.scrollTo({
            top: scrollPosition.current,
        });
    };

    const handleOnSuccessResult = () => {
        dispatch(loadSubscriptions());
    };

    const handleSelectCategory = (category: TFilterCategory) => {
        push(`/subscriptions/${selectedCategory !== category ? category : ''}`);
    };

    const handleScrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const handleClickMultibundleCard = () => {
        showcaseAnalytics.multibundle.details();
    };

    const handleOnShowSpecificCategory = (category: TFilterCategory | undefined, items: TSubscription[]) => {
        if (!category) {
            return;
        }

        showcaseAnalytics.showSpecificCategoryImpressions({
            category,
            items,
            list: 'start',
        });
    };

    const {
        slideWidth,
        cellSpacing,
        slidesToShow,
        cardWidth,
    } = useValueByBreakpoint({
        desktop: { slideWidth: '250px', cardWidth: 237, cellSpacing: 16, slidesToShow: 6 },
        sDesktop: { slideWidth: '187px', cardWidth: 174, cellSpacing: 16, slidesToShow: 6 },
        laptop: { slideWidth: '156px', cardWidth: 143, cellSpacing: 16, slidesToShow: 6 },
        tablet: { slideWidth: '207px', cardWidth: 194, cellSpacing: 12, slidesToShow: 3 },
        mobile: { slideWidth: '197px', cardWidth: 194, cellSpacing: 12, slidesToShow: 1 },
    });

    return (
        <WideContainer>
            <IndentContainer className={className} $desktop={[32, 0]} $mobile={[24, 0]}>
                <H2 $smaller>Все услуги</H2>

                <IndentContainer $desktop={[0, 0, 32]} $mobile={[0, 0, 24]}>
                    <NukaCarousel
                        slideWidth={slideWidth}
                        cellSpacing={cellSpacing}
                        withoutDots
                        slidesToScroll={'auto'}
                        withoutArrows
                        slidesToShow={slidesToShow}
                    >
                        {filters.map(filter => (
                            <WithSkeleton
                                isLoading={loading}
                                width={cardWidth}
                                height={180}
                                borderRadius={BorderRadius.InnerBlockRadius}
                                containerClassName={'react-skeleton-block'}
                                key={`Category__Skeleton__${filter.category}`}
                            >
                                <Category
                                    key={`Category__${filter.category}`}
                                    image={{
                                        src: `/static/images/subscriptions/categories/${filter.category}.png`,
                                    }}
                                    title={filter.title}
                                    data-test={`Showcase__CategoryList__${filter.category}`}
                                    onClick={() => handleSelectCategory(filter.category)}
                                    selected={selectedCategory === filter.category}
                                    width={cardWidth}
                                />
                            </WithSkeleton>
                        ))}
                    </NukaCarousel>
                </IndentContainer>

                <WithSkeletonContext isLoading={loading}>
                    <ListContainer $size={32} $direction={'column'}>
                        {loading
                            ? <ShowcaseSkeleton data-test={'Showcase__ShowcaseSkeleton'} count={count}/>
                            : (
                                <>
                                    {!isAuth && <AuthBanner />}
                                    {specific.map(service => {
                                        const maxCount = count - (service.multibundle ? 1 : 0);
                                        const items = service.items.slice(0, service.showAll ? undefined : maxCount);
                                        const withStub = !service.items.length && !service.multibundle;
                                        const withButtonShowAll = service.items.length > maxCount;
                                        const connectedCount = service.category ? connected[service.category].items.length : 0;

                                        return (
                                            <ServiceCategory
                                                key={service.category}
                                                name={service.name}
                                                description={service.description}
                                                category={service.category}
                                                withStub={withStub}
                                                withButtonShowAll={withButtonShowAll}
                                                showAll={service.showAll}
                                                onToggleShowAll={() => handleToggleShowAll(service)}
                                                onShowCategory={() => handleOnShowSpecificCategory(service.category, items)}
                                                connectedCount={connectedCount}
                                            >
                                                {service.multibundle && (
                                                    <MultibundleCard
                                                        key={`MultibundleCard__${service.multibundle.vasId}`}
                                                        className='vas-subscription-wrapper'
                                                        multibundle={service.multibundle}
                                                        onBtnClick={handleClickMultibundleCard}
                                                    />
                                                )}
                                                <Services items={items} />
                                            </ServiceCategory>
                                        );
                                    })}
                                </>
                            )
                        }
                        <ButtonGroup $justify={'center'}>
                            <WithSkeleton
                                width={180}
                                height={36}
                                borderRadius={BorderRadius.OuterBlockRadius}
                            >
                                <Button
                                    data-test={'Showcase__ButtonScrollToTop'}
                                    size={'small'}
                                    bType={'secondary'}
                                    Icon={<ArrowUp/>}
                                    onClick={handleScrollToTop}
                                >
                                    Вернуться наверх
                                </Button>
                            </WithSkeleton>
                        </ButtonGroup>
                    </ListContainer>
                </WithSkeletonContext>

                <Drawer isOpen={isShowDetails} onClose={handleCloseDrawer}>
                    <ServiceDetails />
                </Drawer>

                <StopperPopup />

                <ActionPopup />

                <AuthFormPopup
                    onClose={() => contextDispatch(setAuthFormPopup({ isOpen: false }))}
                    isOpen={authFormPopup.isOpen}
                    userLoginRoute={authFormPopup.url}
                    btnText='Стать клиентом'
                    authBtnText='Авторизоваться'
                />

                <ActionResultPopup onSuccess={handleOnSuccessResult} />
            </IndentContainer>
        </WideContainer>
    );
};

export default styled(Showcase)`
    width: 100%;

    .react-skeleton-block {
        display: block;
    }

    -webkit-tap-highlight-color: transparent;
`;
